import React, { useContext, useEffect, useState } from "react";
import dayjs from "dayjs";
import { useNavigate } from "react-router-dom";
import { useAppDispatch, useAppSelector } from "../../../../../../app/hooks";
import { formatCurrencyAmount } from "../../../../../../utils/currency";
import { SuccessAlertContext } from "../../../../../../context/SuccessAlertContext";
import { NetworkErrorAlertContext } from "../../../../../../context/NetworkErrorAlert";
import { merchantBusinessKeys } from "../../../../../Settings/settingsSlice";
import { encryptPayloadWithMerchantKeyJIT } from "../../../../../../utils/encryptionAlgorithm";
import { payoutRequestToAsia, setLoading } from "../../../../redux/slice";
import { clearAsiaPayoutData, setStep } from "../../../../redux/formDataSlice";
import { requestPayoutV2Otp } from "../../../../api";
import { getErrorMessage } from "../../../../utils";
import {
  ButtonsWrapper,
  ContentWrapper,
  DetailsBox,
  DetailsContent,
  DetailsRow,
  DetailsSectionHeader,
  DetailsValue,
  EditButton,
  SectionTitle,
} from "../../../shared/styles";
import Loader from "../../../../../../components/common/Loader";
import WalletOTP from "../../../../components/SendFunds/components/WalletOTP";
import { DetailsLabel } from "../../../../../Settlements/SettlementPayout/styles";
import { Button } from "../../../../../../components/common/Button";
import { IDR_DATA_SLICE_KEY, IDR_PAYMENT_METHODS, IDR_WALLET_PROVIDERS } from "../const";
import { buildIndonesiaPayoutPayload } from "../helpers";
import { IndonesiaFormInputs, IndonesiaStepFormProps } from "../types";

const BENEFICIARY_STEP_INDEX = 2;

const IndonesiaReviewForm: React.FC<IndonesiaStepFormProps> = ({ handlePrevious }) => {
  const dispatch = useAppDispatch();
  const navigate = useNavigate();

  const getSavedData: IndonesiaFormInputs = useAppSelector(
    (state) => (state.formData as any)?.asiaPayoutData?.[IDR_DATA_SLICE_KEY] || {}
  );
  const { businessId } = useAppSelector((state) => state.users);
  const { keys } = useAppSelector((state) => state.settings);
  const { loading } = useAppSelector((state) => state.wallets || {});

  const [verifyOTP, setVerifyOTP] = useState(false);

  const { onShowAlert: onShowSuccessAlert } = useContext(SuccessAlertContext) || {};
  const { onShowAlert: onShowErrorAlert } = useContext(NetworkErrorAlertContext) || {};

  useEffect(() => {
    if (businessId) {
      dispatch(merchantBusinessKeys(businessId));
    }
  }, [businessId, dispatch]);

  const publicKey = keys?.data?.publicKey;
  const sourceCurrency = getSavedData?.sourceCurrency;
  const currentDate = dayjs().format("DD.MM.YYYY");
  const paymentMethodLabel = IDR_PAYMENT_METHODS.find(
    (method) => method.value === getSavedData?.paymentMethod
  )?.label;
  const isWallet = getSavedData?.paymentMethod === "wallet";
  const walletProviderLabel = IDR_WALLET_PROVIDERS.find(
    (provider) => provider.value === getSavedData?.walletType
  )?.label;

  const handlePayout = async (otp?: string) => {
    try {
      if (!businessId) {
        onShowErrorAlert?.("", "Business information is not available. Please try again.");
        return;
      }

      const payload = buildIndonesiaPayoutPayload(getSavedData, otp);
      const { encryptedMessage, publicKey: jitPublicKey } = await encryptPayloadWithMerchantKeyJIT(
        businessId,
        payload
      );
      const authToken = jitPublicKey || publicKey;

      const action = await dispatch(
        payoutRequestToAsia({ data: { message: encryptedMessage }, token: authToken })
      );

      if (payoutRequestToAsia.fulfilled.match(action)) {
        onShowSuccessAlert?.("Payment processed successfully");
        dispatch(clearAsiaPayoutData({ country: IDR_DATA_SLICE_KEY }));
        navigate("/wallets");
      } else if (payoutRequestToAsia.rejected.match(action)) {
        onShowErrorAlert?.("", (action.payload as string) || "Payout failed");
      }
    } catch (error) {
      onShowErrorAlert?.("", getErrorMessage(error, "Payout failed"));
    }
  };

  const handleSendOTP = async () => {
    try {
      dispatch(setLoading(true));
      await requestPayoutV2Otp(getSavedData?.reference, publicKey);
      setVerifyOTP(true);
    } catch (error) {
      onShowErrorAlert?.("", getErrorMessage(error, "Failed to send OTP"));
    } finally {
      dispatch(setLoading(false));
    }
  };

  const handleEditStep = (stepIndex: number) => {
    dispatch(setStep(stepIndex));
  };

  return (
    <ContentWrapper>
      <Loader isLoading={loading} />

      {verifyOTP && (
        <WalletOTP
          setVerifyOTP={setVerifyOTP}
          makePayment={handlePayout}
          mode="transaction"
          onResend={handleSendOTP}
        />
      )}

      <DetailsBox>
        <DetailsSectionHeader>
          <SectionTitle>Transaction details</SectionTitle>
          <EditButton onClick={() => handleEditStep(0)}>
            <span>Edit</span>
          </EditButton>
        </DetailsSectionHeader>

        <DetailsContent>
          <DetailsRow>
            <DetailsLabel>Beneficiary receives</DetailsLabel>
            <DetailsValue>{formatCurrencyAmount("IDR", getSavedData?.amount)}</DetailsValue>
          </DetailsRow>

          <DetailsRow>
            <DetailsLabel>Exchange rate</DetailsLabel>
            <DetailsValue>
              1 {sourceCurrency} = {getSavedData?.rate} IDR
            </DetailsValue>
          </DetailsRow>

          <DetailsRow>
            <DetailsLabel>Processing fee</DetailsLabel>
            <DetailsValue>{formatCurrencyAmount(sourceCurrency, getSavedData?.fee)}</DetailsValue>
          </DetailsRow>

          <DetailsRow>
            <DetailsLabel>Total amount charged</DetailsLabel>
            <DetailsValue>
              {formatCurrencyAmount(sourceCurrency, getSavedData?.totalAmount)}
            </DetailsValue>
          </DetailsRow>

          <DetailsRow>
            <DetailsLabel>Payment method</DetailsLabel>
            <DetailsValue>{paymentMethodLabel}</DetailsValue>
          </DetailsRow>

          <DetailsRow>
            <DetailsLabel>Date</DetailsLabel>
            <DetailsValue>{currentDate}</DetailsValue>
          </DetailsRow>

          <DetailsRow>
            <DetailsLabel>Purpose</DetailsLabel>
            <DetailsValue>{getSavedData?.purposeLabel}</DetailsValue>
          </DetailsRow>
        </DetailsContent>

        <DetailsSectionHeader>
          <SectionTitle>Beneficiary details</SectionTitle>
          <EditButton onClick={() => handleEditStep(BENEFICIARY_STEP_INDEX)}>
            <span>Edit</span>
          </EditButton>
        </DetailsSectionHeader>

        <DetailsContent>
          <DetailsRow>
            <DetailsLabel>Beneficiary name</DetailsLabel>
            <DetailsValue>{getSavedData?.accountName}</DetailsValue>
          </DetailsRow>

          {isWallet ? (
            <>
              <DetailsRow>
                <DetailsLabel>eWallet provider</DetailsLabel>
                <DetailsValue>{walletProviderLabel}</DetailsValue>
              </DetailsRow>

              <DetailsRow>
                <DetailsLabel>Account number</DetailsLabel>
                <DetailsValue>{getSavedData?.accountNumber}</DetailsValue>
              </DetailsRow>

              <DetailsRow>
                <DetailsLabel>Phone Number</DetailsLabel>
                <DetailsValue>{getSavedData?.phoneNumber}</DetailsValue>
              </DetailsRow>
            </>
          ) : (
            <>
              <DetailsRow>
                <DetailsLabel>Bank name</DetailsLabel>
                <DetailsValue>{getSavedData?.bankName}</DetailsValue>
              </DetailsRow>

              <DetailsRow>
                <DetailsLabel>Account number</DetailsLabel>
                <DetailsValue>{getSavedData?.accountNumber}</DetailsValue>
              </DetailsRow>

              <DetailsRow>
                <DetailsLabel>Narration</DetailsLabel>
                <DetailsValue>{getSavedData?.narration}</DetailsValue>
              </DetailsRow>
            </>
          )}
        </DetailsContent>
      </DetailsBox>

      <ButtonsWrapper>
        <Button onClick={handlePrevious} theme="alternate" height="48px" width="203px">
          Back
        </Button>
        <Button onClick={handleSendOTP} label="Confirm" height="48px" />
      </ButtonsWrapper>
    </ContentWrapper>
  );
};

export default IndonesiaReviewForm;
